import { z } from "zod";
import { TopolSectionSchema } from "./template/structures/sectionSchema";
import { TopolLanguageSchema } from "./template/languageSchema";
import { topolStyleSchema, defaultStyle } from "./template/styleSchema";
import {
  TopolSettingsSchema,
  defaultSettings,
} from "./template/settingsSchema";
export type {
  TopolTranslation,
  TopolTranslatableBlock,
  TopolTranslationBlock,
  TopolSpecialBlockTranslation,
} from "./template/languageSchema";
export type {
  TopolColumn,
  TopolBlock,
} from "./template/structures/sectionSchema";

export { TopolSectionSchema };

export const TopolVariatOtherConditionSchema = z.object({
  conditionField: z.string().min(1),
  conditionComparator: z.string().min(1),
  conditionValue: z.string(),
});

export const TopolVariantConditionSchema = z.object({
  conditionField: z.string().min(1),
  conditionComparator: z.string().min(1),
  conditionValue: z.string(),
  conditionsSeparator: z.string().optional(),
  otherConditions: TopolVariatOtherConditionSchema.refine((data) => {
    if (data.conditionComparator !== "exist" && data.conditionValue === "") {
      return false;
    }
    return true;
  })
    .array()
    .optional()
    .catch((ctx) => {
      const input = ctx.value;
      if (ctx.issues && Array.isArray(input)) {
        const deletedIndexes: number[] = [];
        for (const issue of ctx.issues) {
          const idx = issue.path?.[0];
          if (typeof idx === "number" && !deletedIndexes.includes(idx)) {
            input.splice(idx - deletedIndexes.length, 1);
            deletedIndexes.push(idx);
          }
        }
        return input;
      }
      return [];
    }),
});

export const TopolSectionVariantVariantSchema = z.record(
  z.string(),
  z.object({
    condition: TopolVariantConditionSchema.refine((data) => {
      if (data.conditionComparator !== "exist" && data.conditionValue === "") {
        return false;
      }
      return true;
    }),
    name: z.string(),
  })
);

export const TopolSectionVariantSchema = z.object({
  sections: z.array(z.string()),
  variants: TopolSectionVariantVariantSchema.array().catch((ctx: any) => {
    const input = ctx.value;
    if (ctx.issues && Array.isArray(input)) {
      const deletedIndexes: number[] = [];
      for (const issue of ctx.issues) {
        const idx = issue.path?.[0];
        if (typeof idx === "number" && !deletedIndexes.includes(idx)) {
          input.splice(idx - deletedIndexes.length, 1);
          deletedIndexes.push(idx);
        }
      }
      return input;
    }
    return [];
  }),
  activeVariant: z.string(),
  elseCondition: z.string(),
  elseVariantUid: z.string().optional(),
});

export const topolTemplateSchema = z.object({
  tagName: z.literal("mj-global-style").catch("mj-global-style"),
  attributes: TopolSettingsSchema.default(defaultSettings),
  children: z.tuple([
    z.object({
      tagName: z.literal("mj-body"),
      attributes: z.object({
        "background-color": z.string().catch("#FFFFFF"),
        containerWidth: z.coerce.number().catch(600),
      }),
      children: TopolSectionSchema.array().min(1),
    }),
  ]),
  fonts: z.array(z.string()).optional().catch(undefined),
  style: topolStyleSchema.default(defaultStyle),
  sectionVariants: TopolSectionVariantSchema.refine((data) => {
    if (data.variants.length === 0) {
      return false;
    }
    return true;
  })
    .array()
    .optional()
    .catch((ctx: any) => {
      const input = ctx.value;
      if (ctx.issues && Array.isArray(input)) {
        const deletedIndexes: number[] = [];
        for (const issue of ctx.issues) {
          const idx = issue.path?.[0];
          if (typeof idx === "number" && !deletedIndexes.includes(idx)) {
            input.splice(idx - deletedIndexes.length, 1);
            deletedIndexes.push(idx);
          }
        }
        if (input.length === 0) {
          return undefined;
        }
        return input;
      }
      return undefined;
    }),
  feedBlocksApplyToAll: z.record(z.string(), z.boolean()).catch({}),
  langs: TopolLanguageSchema.optional().catch(undefined),
});

export type TopolTemplate = z.infer<typeof topolTemplateSchema>;
export type TopolTag = keyof TopolTemplate["style"];
export type TopolSection = z.infer<typeof TopolSectionSchema>;
